import { useState, useEffect } from "react";
import Select from "react-select";
import { Button, Col, Row, FormGroup, Label, Badge, Container, Spinner, Card, CardHeader, CardBody, Collapse, Input } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { findAllVeloxityAudience } from "../../../slices/veloxity/audience/FindAllAudienceReducer";
import { getAllTargetAudience } from "../../../slices/veloxity/targetAudience/getAllTargetAudience";
import { listApplications } from "../../../slices/veloxity/pushNotification/listApplication";
import { FaArrowRight, FaAppStore, FaAndroid, FaCheck, FaChevronDown, FaChevronUp } from "react-icons/fa";

const WhoComponent = ({ setSelectedIcon, updateCampaignData, campaignData }) => {
    const dispatch = useDispatch();
    
    const [campaignType, setCampaignType] = useState(() => {
        if (campaignData.campaignType) {
            return { 
                value: campaignData.campaignType, 
                label: campaignData.campaignType === 'realTime' ? 'Real-Time Campaign' : 'Scheduled Campaign' 
            };
        }
        return null;
    });
    
    const [selectedProfiles, setSelectedProfiles] = useState(() => {
        if (campaignData.eventDefinition?.profiles) {
            return campaignData.eventDefinition.profiles.map(p => ({ 
                value: p, 
                label: p === 'WHITE_COLLAR' ? 'White Collar' : p 
            }));
        }
        return [];
    });
    
    const [selectedGenders, setSelectedGenders] = useState(() => {
        if (campaignData.eventDefinition?.genderTypes) {
            return campaignData.eventDefinition.genderTypes.map(g => ({ 
                value: g, 
                label: g === 'MALE' ? 'Male' : 'Female' 
            }));
        }
        return [];
    });
    
    const [selectedGenerations, setSelectedGenerations] = useState(() => {
        if (campaignData.eventDefinition?.generationTypes) {
            return campaignData.eventDefinition.generationTypes.map(g => {
                let label = g;
                switch(g) {
                    case 'X': label = 'Generation X'; break;
                    case 'Y': label = 'Generation Y'; break;
                    case 'Z': label = 'Generation Z'; break;
                    case 'ALPHA': label = 'Generation Alpha'; break;
                    case 'BABY_BOOMER': label = 'Baby Boomer'; break;
                    default: label = g;
                }
                return { value: g, label };
            });
        }
        return [];
    });
    
    const [delayMs, setDelayMs] = useState(() => {
        if (campaignData.eventDefinition?.delayMs) {
            return campaignData.eventDefinition.delayMs.toString();
        }
        return '';
    });
    
    const [selectedApp, setSelectedApp] = useState(campaignData.selectedApp || null);
    const [selectedDeviceOS, setSelectedDeviceOS] = useState(campaignData.selectedDeviceOS || null);
    const [selectedAudience, setSelectedAudience] = useState(campaignData.selectedAudience || null);
    
    const [openSections, setOpenSections] = useState({
        campaignType: true,
        targetingCriteria: true,
        delaySettings: true,
        appSelection: true,
        deviceOS: true,
        audience: true
    });

    const regularAudiences = useSelector((state) => state.findAllAudienceReducer?.payload || []);
    const targetAudiences = useSelector((state) => state.targetAllAudience?.payload || []);
    const applications = useSelector((state) => state.listApplications?.applications || []);
    const appsLoading = useSelector((state) => state.listApplications?.loading);
    const audienceLoading = useSelector(
        (state) => state.findAllAudienceReducer?.loading || state.targetAllAudience?.loading
    );

    const profileOptions = [
        { value: 'WHITE_COLLAR', label: 'White Collar' }
    ];

    const genderOptions = [
        { value: 'MALE', label: 'Male' },
        { value: 'FEMALE', label: 'Female' }
    ];

    const generationOptions = [
        { value: 'X', label: 'Generation X' },
        { value: 'Y', label: 'Generation Y' },
        { value: 'Z', label: 'Generation Z' },
        { value: 'ALPHA', label: 'Generation Alpha' },
        { value: 'BABY_BOOMER', label: 'Baby Boomer' }
    ];

    useEffect(() => {
        dispatch(listApplications());
        dispatch(findAllVeloxityAudience());
        dispatch(getAllTargetAudience());
    }, [dispatch]);

    useEffect(() => {
        if (campaignType) {
            if (campaignType.value === 'realTime') {
                updateCampaignData({
                    campaignType: campaignType.value,
                    eventDefinition: {
                        profiles: selectedProfiles.map(p => p.value),
                        genderTypes: selectedGenders.map(g => g.value),
                        generationTypes: selectedGenerations.map(g => g.value)
                    }
                });
            } else {
                updateCampaignData({
                    campaignType: campaignType.value,
                    eventDefinition: {
                        delayMs: delayMs ? parseInt(delayMs, 10) : null
                    }
                });
            }
        }
    }, [campaignType, selectedProfiles, selectedGenders, selectedGenerations, delayMs, updateCampaignData]);

    const isCampaignTypeSelected = campaignType !== null;
    const isDelaySettingsComplete = campaignType?.value === 'scheduled' ? 
        delayMs !== '' && parseInt(delayMs, 10) > 0 && parseInt(delayMs, 10) <= 2000 : true;
    const isTargetingCriteriaComplete = campaignType?.value === 'realTime' ? 
        (selectedProfiles.length > 0 && selectedGenders.length > 0 && selectedGenerations.length > 0) : true;
    const isAppSelectionComplete = selectedApp !== null;
    const isDeviceOSComplete = selectedDeviceOS !== null;
    const isAudienceComplete = selectedAudience !== null;

    useEffect(() => {
        if (isCampaignTypeSelected) {
            setOpenSections(prev => ({ ...prev, campaignType: false }));
            
            if (campaignType.value === 'scheduled') {
                setOpenSections(prev => ({ ...prev, delaySettings: true }));
            } else if (campaignType.value === 'realTime') {
                setOpenSections(prev => ({ ...prev, targetingCriteria: true }));
            }
        }
    }, [isCampaignTypeSelected, campaignType]);

    useEffect(() => {
        if (isDelaySettingsComplete && campaignType?.value === 'scheduled' && delayMs.length >= 4) {
            setOpenSections(prev => ({ ...prev, delaySettings: false, appSelection: true }));
        }
    }, [isDelaySettingsComplete, campaignType, delayMs]);

    useEffect(() => {
        if (isTargetingCriteriaComplete && campaignType?.value === 'realTime') {
            setOpenSections(prev => ({ ...prev, targetingCriteria: false, appSelection: true }));
        }
    }, [isTargetingCriteriaComplete, campaignType]);

    useEffect(() => {
        if (isAppSelectionComplete) {
            setOpenSections(prev => ({ ...prev, appSelection: false, deviceOS: true }));
        }
    }, [isAppSelectionComplete]);

    useEffect(() => {
        if (isDeviceOSComplete) {
            setOpenSections(prev => ({ ...prev, deviceOS: false, audience: true }));
        }
    }, [isDeviceOSComplete]);

    useEffect(() => {
        if (isAudienceComplete) {
            setOpenSections(prev => ({ ...prev, audience: false }));
        }
    }, [isAudienceComplete]);

    const prepareAppOptions = () => {
        return applications.map(app => ({
            value: app.id || app.applicationId,
            label: app.name,
            originalData: app
        }));
    };

    const CAMPAIGN_TYPE_OPTIONS = [
        { value: 'scheduled', label: 'Scheduled Campaign' },
        { value: 'realTime', label: 'Real-Time Campaign' }
    ];

    const ACTIVE_OPTIONS = [
        { value: 'ios', label: 'iOS' },
        { value: 'android', label: 'Android' },
        { value: 'both', label: 'Both' }
    ];

    const prepareAudienceOptions = () => {
        const allAudiences = [
            ...regularAudiences.map(audience => ({
                value: audience.audienceId || audience.id,
                label: audience.name,
                type: "regular",
                originalData: audience
            })),
            ...targetAudiences.map(audience => ({
                value: audience.audienceId || audience.id,
                label: audience.name,
                type: "target",
                originalData: audience
            }))
        ];
        
        return allAudiences;
    };
    
    const isNextDisabled = !isCampaignTypeSelected || !isDelaySettingsComplete || !isTargetingCriteriaComplete || 
        !isAppSelectionComplete || !isDeviceOSComplete || !isAudienceComplete;

    const formatAppOptionLabel = ({ label, platform }) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{label}</span>
            {platform && (
                <Badge color={platform.toLowerCase() === 'ios' ? 'info' : 'success'} pill>
                    {platform.toLowerCase() === 'ios' ? <FaAppStore className="me-1" /> : <FaAndroid className="me-1" />}
                    {platform}
                </Badge>
            )}
        </div>
    );

    const formatAudienceOptionLabel = ({ label, type }) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{label}</span>
            <Badge color={type === "regular" ? "info" : "warning"} pill>
                {type === "regular" ? "Audience" : "Target Audience"}
            </Badge>
        </div>
    );

    const handleNext = () => {
        const updatedData = {
            campaignType: campaignType.value,
            selectedApp,
            selectedDeviceOS,
            selectedAudience,
            eventDefinition: campaignType.value === 'realTime' 
                ? {
                    profiles: selectedProfiles.map(p => p.value),
                    genderTypes: selectedGenders.map(g => g.value),
                    generationTypes: selectedGenerations.map(g => g.value)
                } 
                : {
                    delayMs: parseInt(delayMs, 10)
                }
        };
        

        updateCampaignData(updatedData);
        setSelectedIcon(1);
    };

    const handleProfileChange = (selectedOptions) => {
        setSelectedProfiles(selectedOptions || []);
    };

    const handleGenderChange = (selectedOptions) => {
        setSelectedGenders(selectedOptions || []);
    };

    const handleGenerationChange = (selectedOptions) => {
        setSelectedGenerations(selectedOptions || []);
    };

    const handleDelayChange = (e) => {
        const value = e.target.value;
        if (value === '' || !isNaN(parseInt(value, 10))) {
            if (value === '' || parseInt(value, 10) <= 2000) {
                setDelayMs(value);
            }
        }
    };

    const toggleSection = (section) => {
        setOpenSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            backgroundColor: '#fff',
            borderColor: '#ced4da'
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#CC1977' : state.isFocused ? '#f8f9fa' : null,
            color: state.isSelected ? 'white' : 'black'
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: '#CC1977'
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: 'white',
            fontWeight: 500
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: 'white',
            ':hover': {
                backgroundColor: '#a01460',
                color: 'white'
            }
        })
    };

    return (
        <div className="who-component">
            <Container fluid>
                {/* Campaign Type Section */}
                <Card className="mb-3">
                    <CardHeader 
                        className={`d-flex align-items-center ${isCampaignTypeSelected ? 'completed-header' : ''}`}
                        onClick={() => toggleSection('campaignType')}
                        style={{ cursor: 'pointer' }}
                    >
                        <div className={`step-number ${isCampaignTypeSelected ? 'completed' : ''}`}>
                            {isCampaignTypeSelected ? <FaCheck /> : '1'}
                        </div>
                        <h5 className="mb-0">Campaign Type</h5>
                        <div className="ms-auto">
                            {openSections.campaignType ? <FaChevronUp /> : <FaChevronDown />}
                        </div>
                    </CardHeader>
                    <Collapse isOpen={openSections.campaignType}>
                        <CardBody>
                            <FormGroup>
                                <Label>Select Campaign Type</Label>
                                <Select
                                    options={CAMPAIGN_TYPE_OPTIONS}
                                    value={campaignType}
                                    onChange={(option) => setCampaignType(option)}
                                    placeholder="Select campaign type"
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isClearable
                                />
                            </FormGroup>
                        </CardBody>
                    </Collapse>
                </Card>

                {/* Delay Settings Section - Only for Scheduled Campaign */}
                {campaignType?.value === 'scheduled' && (
                    <Card className={`mb-3 ${!isCampaignTypeSelected ? 'disabled-card' : ''}`}>
                        <CardHeader 
                            className={`d-flex align-items-center ${isDelaySettingsComplete ? 'completed-header' : ''}`}
                            onClick={() => isCampaignTypeSelected && toggleSection('delaySettings')}
                            style={{ cursor: isCampaignTypeSelected ? 'pointer' : 'not-allowed' }}
                        >
                            <div className={`step-number ${isDelaySettingsComplete ? 'completed' : ''}`}>
                                {isDelaySettingsComplete ? <FaCheck /> : '2'}
                            </div>
                            <h5 className="mb-0">Delay Settings</h5>
                            <div className="ms-auto">
                                {openSections.delaySettings ? <FaChevronUp /> : <FaChevronDown />}
                            </div>
                        </CardHeader>
                        <Collapse isOpen={openSections.delaySettings && isCampaignTypeSelected}>
                            <CardBody>
                                <FormGroup>
                                    <Label for="delayMs">Delay (milliseconds) - Max 2000ms</Label>
                                    <Input
                                        type="number"
                                        id="delayMs"
                                        name="delayMs"
                                        value={delayMs}
                                        onChange={handleDelayChange}
                                        min="0"
                                        max="2000"
                                        placeholder="Enter delay in milliseconds"
                                    />
                                    <small className="text-muted">
                                        Enter a delay between 0 and 2000 milliseconds (2 seconds)
                                    </small>
                                    {delayMs !== '' && parseInt(delayMs, 10) > 2000 && (
                                        <div className="text-danger mt-1">
                                            Maximum delay is 2000ms (2 seconds)
                                        </div>
                                    )}
                                </FormGroup>
                            </CardBody>
                        </Collapse>
                    </Card>
                )}

                {/* Targeting Criteria Section - Only for Real-Time Campaign */}
                {campaignType?.value === 'realTime' && (
                    <Card className={`mb-3 ${!isCampaignTypeSelected ? 'disabled-card' : ''}`}>
                        <CardHeader 
                            className={`d-flex align-items-center ${isTargetingCriteriaComplete ? 'completed-header' : ''}`}
                            onClick={() => isCampaignTypeSelected && toggleSection('targetingCriteria')}
                            style={{ cursor: isCampaignTypeSelected ? 'pointer' : 'not-allowed' }}
                        >
                            <div className={`step-number ${isTargetingCriteriaComplete ? 'completed' : ''}`}>
                                {isTargetingCriteriaComplete ? <FaCheck /> : '2'}
                            </div>
                            <h5 className="mb-0">Targeting Criteria</h5>
                            <div className="ms-auto">
                                {openSections.targetingCriteria ? <FaChevronUp /> : <FaChevronDown />}
                            </div>
                        </CardHeader>
                        <Collapse isOpen={openSections.targetingCriteria && isCampaignTypeSelected}>
                            <CardBody>
                                <Row>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Profiles</Label>
                                            <Select
                                                isMulti
                                                options={profileOptions}
                                                value={selectedProfiles}
                                                onChange={handleProfileChange}
                                                placeholder="Select profiles..."
                                                styles={customSelectStyles}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Genders</Label>
                                            <Select
                                                isMulti
                                                options={genderOptions}
                                                value={selectedGenders}
                                                onChange={handleGenderChange}
                                                placeholder="Select genders..."
                                                styles={customSelectStyles}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Generations</Label>
                                            <Select
                                                isMulti
                                                options={generationOptions}
                                                value={selectedGenerations}
                                                onChange={handleGenerationChange}
                                                placeholder="Select generations..."
                                                styles={customSelectStyles}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Collapse>
                    </Card>
                )}

                {/* App Selection Section */}
                <Card className={`mb-3 ${!isCampaignTypeSelected ? 'disabled-card' : ''}`}>
                    <CardHeader 
                        className={`d-flex align-items-center ${isAppSelectionComplete ? 'completed-header' : ''}`}
                        onClick={() => isCampaignTypeSelected && toggleSection('appSelection')}
                        style={{ cursor: isCampaignTypeSelected ? 'pointer' : 'not-allowed' }}
                    >
                        <div className={`step-number ${isAppSelectionComplete ? 'completed' : ''}`}>
                            {isAppSelectionComplete ? <FaCheck /> : campaignType?.value === 'realTime' ? '3' : '3'}
                        </div>
                        <h5 className="mb-0">App Selection</h5>
                        <div className="ms-auto">
                            {openSections.appSelection ? <FaChevronUp /> : <FaChevronDown />}
                        </div>
                    </CardHeader>
                    <Collapse isOpen={openSections.appSelection && isCampaignTypeSelected}>
                        <CardBody>
                            <FormGroup>
                                <Label>Select Application</Label>
                                {appsLoading ? (
                                    <div className="text-center my-3">
                                        <Spinner size="sm" color="primary" /> Loading applications...
                                    </div>
                                ) : (
                                    <Select
                                        options={prepareAppOptions()}
                                        value={selectedApp}
                                        onChange={setSelectedApp}
                                        placeholder="Select an application"
                                        formatOptionLabel={formatAppOptionLabel}
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        isDisabled={!isCampaignTypeSelected}
                                        isClearable
                                    />
                                )}
                            </FormGroup>
                        </CardBody>
                    </Collapse>
                </Card>

                {/* Device OS Section */}
                <Card className={`mb-3 ${!isAppSelectionComplete ? 'disabled-card' : ''}`}>
                    <CardHeader 
                        className={`d-flex align-items-center ${isDeviceOSComplete ? 'completed-header' : ''}`}
                        onClick={() => isAppSelectionComplete && toggleSection('deviceOS')}
                        style={{ cursor: isAppSelectionComplete ? 'pointer' : 'not-allowed' }}
                    >
                        <div className={`step-number ${isDeviceOSComplete ? 'completed' : ''}`}>
                            {isDeviceOSComplete ? <FaCheck /> : campaignType?.value === 'realTime' ? '4' : '4'}
                        </div>
                        <h5 className="mb-0">Device OS</h5>
                        <div className="ms-auto">
                            {openSections.deviceOS ? <FaChevronUp /> : <FaChevronDown />}
                        </div>
                    </CardHeader>
                    <Collapse isOpen={openSections.deviceOS && isAppSelectionComplete}>
                        <CardBody>
                            <FormGroup>
                                <Label>Select Device OS</Label>
                                <Select
                                    options={ACTIVE_OPTIONS}
                                    value={selectedDeviceOS}
                                    onChange={setSelectedDeviceOS}
                                    placeholder="Select device OS"
                                    isDisabled={!isAppSelectionComplete}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isClearable
                                />
                            </FormGroup>
                        </CardBody>
                    </Collapse>
                </Card>

                {/* Audience Section */}
                <Card className={`mb-3 ${!isDeviceOSComplete ? 'disabled-card' : ''}`}>
                    <CardHeader 
                        className={`d-flex align-items-center ${isAudienceComplete ? 'completed-header' : ''}`}
                        onClick={() => isDeviceOSComplete && toggleSection('audience')}
                        style={{ cursor: isDeviceOSComplete ? 'pointer' : 'not-allowed' }}
                    >
                        <div className={`step-number ${isAudienceComplete ? 'completed' : ''}`}>
                            {isAudienceComplete ? <FaCheck /> : campaignType?.value === 'realTime' ? '5' : '5'}
                        </div>
                        <h5 className="mb-0">Audience</h5>
                        <div className="ms-auto">
                            {openSections.audience ? <FaChevronUp /> : <FaChevronDown />}
                        </div>
                    </CardHeader>
                    <Collapse isOpen={openSections.audience && isDeviceOSComplete}>
                        <CardBody>
                            <FormGroup>
                                <Label>Select Audience</Label>
                                {audienceLoading ? (
                                    <div className="text-center my-3">
                                        <Spinner size="sm" color="primary" /> Loading audiences...
                                    </div>
                                ) : (
                                    <Select
                                        options={prepareAudienceOptions()}
                                        value={selectedAudience}
                                        onChange={setSelectedAudience}
                                        placeholder="Select an audience"
                                        formatOptionLabel={formatAudienceOptionLabel}
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        isDisabled={!isDeviceOSComplete}
                                        isClearable
                                    />
                                )}
                            </FormGroup>
                        </CardBody>
                    </Collapse>
                </Card>

                {/* Next Button */}
                <Row className="mb-4">
                    <Col md={12} className="text-end">
                        <Button 
                            color="primary" 
                            className="next-button" 
                            onClick={handleNext}
                            disabled={isNextDisabled}
                        >
                            Next: When <FaArrowRight className="ms-2" />
                        </Button>
                    </Col>
                </Row>
            </Container>

            <style jsx="true">{`
                .who-component {
                    padding: 15px 0;
                    width: 100%;
                }
                
                .step-number {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 28px;
                    height: 28px;
                    border-radius: 50%;
                    background-color: #e9ecef;
                    color: #6c757d;
                    margin-right: 10px;
                    font-size: 14px;
                    font-weight: 600;
                    transition: all 0.3s ease;
                }
                
                .step-number.completed {
                    background-color: #28a745;
                    color: white;
                }
                
                .disabled-card {
                    opacity: 0.7;
                    pointer-events: none;
                }
                
                .card-header {
                    transition: all 0.3s ease;
                }
                
                .card-header:hover {
                    background-color: #f8f9fa;
                }
                
                .completed-header {
                    border-left: 4px solid #28a745;
                    background-color: #f8fff8;
                }
            `}</style>
        </div>
    );
}

export default WhoComponent; 